import React from 'react';
import { Star, Plus, Trash2 } from 'lucide-react';
import EditableText from './EditableText';
import ImageUpload from './ImageUpload';

const SettingsAdmin = ({ settings, onSave, iconOptions }) => {
  const promoTiles = settings.promo_tiles || [];
  const footerLinks = settings.footer_links || {};

  const updateTile = (idx, patch) => {
    const newTiles = promoTiles.map((t, i) => i === idx ? { ...t, ...patch } : t);
    return onSave({ promo_tiles: newTiles });
  };
  const addTile = () => onSave({ promo_tiles: [...promoTiles, { title: 'New tile', text: 'Description', icon: 'Sparkles' }] });
  const removeTile = (idx) => onSave({ promo_tiles: promoTiles.filter((_, i) => i !== idx) });

  const updateFooterCol = (oldTitle, newTitle) => {
    const next = {};
    Object.entries(footerLinks).forEach(([k, v]) => { next[k === oldTitle ? newTitle : k] = v; });
    return onSave({ footer_links: next });
  };
  const updateFooterItem = (colTitle, idx, newVal) => {
    const next = { ...footerLinks, [colTitle]: footerLinks[colTitle].map((x, i) => i === idx ? newVal : x) };
    return onSave({ footer_links: next });
  };
  const addFooterItem = (colTitle) => onSave({ footer_links: { ...footerLinks, [colTitle]: [...(footerLinks[colTitle] || []), 'New link'] } });
  const removeFooterItem = (colTitle, idx) => onSave({ footer_links: { ...footerLinks, [colTitle]: footerLinks[colTitle].filter((_, i) => i !== idx) } });
  const addFooterCol = () => onSave({ footer_links: { ...footerLinks, [`New column ${Object.keys(footerLinks).length + 1}`]: ['Link 1', 'Link 2'] } });
  const removeFooterCol = (title) => {
    const next = { ...footerLinks }; delete next[title];
    return onSave({ footer_links: next });
  };

  return (
    <div className="space-y-6" data-testid="settings-admin">
      <section className="bg-white border border-neutral-200 rounded-sm p-5">
        <h3 className="text-lg font-bold text-[#12263A] mb-4 flex items-center gap-2"><Star className="w-5 h-5 text-[#F46A25]" /> Brand</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <div>
              <span className="text-xs uppercase text-neutral-500 font-semibold">Brand name</span>
              <EditableText value={settings.brand_name || 'Dr Tools'} onSave={(v) => onSave({ brand_name: v })} className="font-bold text-lg" testid="settings-brand-name" />
            </div>
            <div>
              <span className="text-xs uppercase text-neutral-500 font-semibold">Website</span>
              <EditableText value={settings.website || ''} onSave={(v) => onSave({ website: v })} testid="settings-website" />
            </div>
            <div>
              <span className="text-xs uppercase text-neutral-500 font-semibold">Tagline</span>
              <EditableText value={settings.tagline || ''} onSave={(v) => onSave({ tagline: v })} testid="settings-tagline" />
            </div>
          </div>
          <div>
            <span className="text-xs uppercase text-neutral-500 font-semibold mb-2 block">Company logo</span>
            <div className="max-w-[240px]">
              <ImageUpload src={settings.logo_url} alt="Logo" ratio="aspect-video" onChange={(u) => onSave({ logo_url: u })} />
            </div>
            <p className="text-[11px] text-neutral-500 mt-2" data-testid="settings-logo-hint">Recommended: transparent PNG, at least 400×160. Replaces the site header & Quote PDF logo everywhere.</p>
          </div>
        </div>
      </section>

      <section className="bg-white border border-neutral-200 rounded-sm p-5">
        <h3 className="text-lg font-bold text-[#12263A] mb-4">Company details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <span className="text-xs uppercase text-neutral-500 font-semibold">Company address</span>
            <textarea
              value={settings.company_address || ''}
              onChange={e => onSave({ company_address: e.target.value })}
              rows={3}
              data-testid="settings-company-address"
              className="w-full mt-1 border border-neutral-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#F46A25]"
              placeholder="1 Trade Way, Manchester M1 1AA, United Kingdom"
            />
          </div>
          <div className="space-y-3">
            <div>
              <span className="text-xs uppercase text-neutral-500 font-semibold">Phone</span>
              <EditableText value={settings.company_phone || ''} onSave={(v) => onSave({ company_phone: v })} testid="settings-company-phone" />
            </div>
            <div>
              <span className="text-xs uppercase text-neutral-500 font-semibold">Email</span>
              <EditableText value={settings.company_email || ''} onSave={(v) => onSave({ company_email: v })} testid="settings-company-email" />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white border border-neutral-200 rounded-sm p-5">
        <h3 className="text-lg font-bold text-[#12263A] mb-2">Hero carousel</h3>
        <p className="text-xs text-neutral-500 mb-4">Upload banners in the "Hero Banners" tab. Set the auto-advance timer below.</p>
        <div className="max-w-xs">
          <span className="text-xs uppercase text-neutral-500 font-semibold">Slide duration (seconds)</span>
          <input
            type="number"
            min="2"
            max="30"
            step="1"
            defaultValue={Math.round((settings.hero_slide_duration_ms || 6000) / 1000)}
            onBlur={e => {
              const secs = Math.max(2, Math.min(30, Number(e.target.value) || 6));
              onSave({ hero_slide_duration_ms: secs * 1000 });
            }}
            data-testid="settings-slide-duration"
            className="w-full mt-1 border border-neutral-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#F46A25]"
          />
          <p className="text-[11px] text-neutral-500 mt-1">Between 2 and 30 seconds. Applies to the home-page hero banner site-wide.</p>
        </div>
      </section>

      <section className="bg-white border border-neutral-200 rounded-sm p-5">
        <h3 className="text-lg font-bold text-[#12263A] mb-2">Global shipping</h3>
        <p className="text-xs text-neutral-500 mb-4">
          Applies to any product that does not have its own per-product shipping override. Leaving these blank restores the historical defaults (£75 / £4.95).
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <span className="text-xs uppercase text-neutral-500 font-semibold">Free delivery threshold (£)</span>
            <input
              type="number"
              min="0"
              step="0.01"
              defaultValue={settings.free_delivery_threshold ?? ''}
              onBlur={e => {
                const raw = e.target.value.trim();
                const val = raw === '' ? null : Math.max(0, Number(raw));
                onSave({ free_delivery_threshold: val });
              }}
              data-testid="settings-free-delivery-threshold"
              className="w-full mt-1 border border-neutral-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#F46A25]"
              placeholder="75.00"
            />
            <p className="text-[11px] text-neutral-500 mt-1">Orders at or above this subtotal (ex VAT) ship free.</p>
          </div>
          <div>
            <span className="text-xs uppercase text-neutral-500 font-semibold">Standard delivery fee (£)</span>
            <input
              type="number"
              min="0"
              step="0.01"
              defaultValue={settings.standard_delivery_fee ?? ''}
              onBlur={e => {
                const raw = e.target.value.trim();
                const val = raw === '' ? null : Math.max(0, Number(raw));
                onSave({ standard_delivery_fee: val });
              }}
              data-testid="settings-standard-delivery-fee"
              className="w-full mt-1 border border-neutral-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#F46A25]"
              placeholder="4.95"
            />
            <p className="text-[11px] text-neutral-500 mt-1">Charged on any order below the free-delivery threshold.</p>
          </div>
        </div>
      </section>

      <section className="bg-white border border-neutral-200 rounded-sm p-5" data-testid="settings-retargeting-section">
        <h3 className="text-lg font-bold text-[#12263A] mb-2">Smart AI Retargeting</h3>
        <p className="text-xs text-neutral-500 mb-4">
          Auto-sends follow-up emails to signed-in customers who browsed a product or left items in their basket without buying. Master toggle keeps the whole system off until you're ready.
        </p>
        <label className="flex items-center gap-2 mb-4 cursor-pointer" data-testid="settings-retargeting-enabled-label">
          <input
            type="checkbox"
            defaultChecked={!!settings.retargeting_enabled}
            onChange={e => onSave({ retargeting_enabled: e.target.checked })}
            data-testid="settings-retargeting-enabled"
            className="w-4 h-4"
          />
          <span className="text-sm font-bold text-[#12263A]">Enable retargeting emails</span>
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <span className="text-xs uppercase text-neutral-500 font-semibold">Product-view delay (hours)</span>
            <input
              type="number"
              min="1"
              step="1"
              defaultValue={settings.retargeting_view_delay_hours ?? 24}
              onBlur={e => {
                const raw = e.target.value.trim();
                const val = raw === '' ? 24 : Math.max(1, Math.floor(Number(raw)));
                onSave({ retargeting_view_delay_hours: val });
              }}
              data-testid="settings-retargeting-view-delay"
              className="w-full mt-1 border border-neutral-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#F46A25]"
              placeholder="24"
            />
            <p className="text-[11px] text-neutral-500 mt-1">Wait this many hours after a user views a product before sending the "still thinking?" follow-up.</p>
          </div>
          <div>
            <span className="text-xs uppercase text-neutral-500 font-semibold">Cart-abandon delay (hours)</span>
            <input
              type="number"
              min="1"
              step="1"
              defaultValue={settings.retargeting_cart_delay_hours ?? 6}
              onBlur={e => {
                const raw = e.target.value.trim();
                const val = raw === '' ? 6 : Math.max(1, Math.floor(Number(raw)));
                onSave({ retargeting_cart_delay_hours: val });
              }}
              data-testid="settings-retargeting-cart-delay"
              className="w-full mt-1 border border-neutral-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#F46A25]"
              placeholder="6"
            />
            <p className="text-[11px] text-neutral-500 mt-1">Wait this many hours after a basket is idle before nudging the customer back.</p>
          </div>
        </div>
        <p className="text-[11px] text-neutral-500 mt-3 italic">
          Emails use the <b>retargeting_view</b> and <b>retargeting_cart</b> templates — edit copy in the Emails tab.
        </p>
      </section>

      <section className="bg-white border border-neutral-200 rounded-sm p-5">
        <h3 className="text-lg font-bold text-[#12263A] mb-2">Marketing tags</h3>
        <p className="text-xs text-neutral-500 mb-4">
          Enter your Google Tag (GTAG) and Meta Pixel IDs. Tags fire only <b>after</b> a visitor consents via the cookie banner (Analytics ⇒ GTAG, Marketing ⇒ Meta Pixel). Leave blank to disable.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <span className="text-xs uppercase text-neutral-500 font-semibold">Google Tag ID</span>
            <input
              type="text"
              defaultValue={settings.gtag_id || ''}
              onBlur={e => onSave({ gtag_id: e.target.value.trim() })}
              data-testid="settings-gtag-id"
              className="w-full mt-1 border border-neutral-300 rounded-sm px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#F46A25]"
              placeholder="G-XXXXXXXXXX  or  AW-XXXXXXXXX"
            />
            <p className="text-[11px] text-neutral-500 mt-1">Find in Google Analytics 4 → Admin → Data Streams. Also accepts Google Ads AW- IDs.</p>
          </div>
          <div>
            <span className="text-xs uppercase text-neutral-500 font-semibold">Meta Pixel ID</span>
            <input
              type="text"
              defaultValue={settings.meta_pixel_id || ''}
              onBlur={e => onSave({ meta_pixel_id: e.target.value.trim() })}
              data-testid="settings-meta-pixel-id"
              className="w-full mt-1 border border-neutral-300 rounded-sm px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#F46A25]"
              placeholder="e.g. 123456789012345"
            />
            <p className="text-[11px] text-neutral-500 mt-1">Numeric ID from Facebook Events Manager → Data Sources → your pixel.</p>
          </div>
        </div>
      </section>

      <section className="bg-white border border-neutral-200 rounded-sm p-5">
        <h3 className="text-lg font-bold text-[#12263A] mb-2">Product reviews</h3>
        <p className="text-xs text-neutral-500 mb-4">
          Controls what customers see under each approved review. The time of day is never shown — reviews only ever display the date (or nothing).
        </p>
        <label className="flex items-start gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={settings.show_reviews_date !== false}
            onChange={e => onSave({ show_reviews_date: e.target.checked })}
            data-testid="settings-show-reviews-date"
            className="mt-0.5 w-4 h-4 accent-[#F46A25]"
          />
          <span className="text-sm text-[#12263A]">
            <b>Show date on reviews</b>
            <span className="block text-[11px] text-neutral-500 mt-0.5">When off, the date is hidden from customers. Admins always see it in the moderation queue.</span>
          </span>
        </label>
      </section>

      <section className="bg-white border border-neutral-200 rounded-sm p-5">
        <h3 className="text-lg font-bold text-[#12263A] mb-2">Social media accounts</h3>
        <p className="text-xs text-neutral-500 mb-4">Paste the full profile URL. Empty fields hide the icon from the site footer.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            { key: 'social_facebook', label: 'Facebook', placeholder: 'https://facebook.com/drtoolsuk' },
            { key: 'social_instagram', label: 'Instagram', placeholder: 'https://instagram.com/drtoolsuk' },
            { key: 'social_twitter', label: 'X / Twitter', placeholder: 'https://x.com/drtoolsuk' },
            { key: 'social_linkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/company/drtoolsuk' },
            { key: 'social_youtube', label: 'YouTube', placeholder: 'https://youtube.com/@drtoolsuk' },
            { key: 'social_tiktok', label: 'TikTok', placeholder: 'https://tiktok.com/@drtoolsuk' },
          ].map(f => (
            <div key={f.key}>
              <span className="text-xs uppercase text-neutral-500 font-semibold">{f.label}</span>
              <input
                type="url"
                value={settings[f.key] || ''}
                onChange={e => onSave({ [f.key]: e.target.value })}
                placeholder={f.placeholder}
                data-testid={`settings-${f.key}`}
                className="w-full mt-1 border border-neutral-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#F46A25]"
              />
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white border border-neutral-200 rounded-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-[#12263A]">Promo strip (homepage)</h3>
          <button onClick={addTile} className="text-xs bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold px-3 py-1.5 rounded-sm flex items-center gap-1"><Plus className="w-3 h-3" /> Add tile</button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {promoTiles.map((t, i) => (
            <div key={i} className="border border-neutral-200 rounded-sm p-3 relative">
              <button onClick={() => removeTile(i)} className="absolute top-2 right-2 text-red-600 hover:bg-red-50 p-1 rounded" aria-label="Remove"><Trash2 className="w-3.5 h-3.5" /></button>
              <div className="space-y-2 text-sm">
                <div><span className="text-[11px] uppercase text-neutral-500 font-semibold">Title</span>
                  <EditableText value={t.title} onSave={(v) => updateTile(i, { title: v })} className="font-semibold" /></div>
                <div><span className="text-[11px] uppercase text-neutral-500 font-semibold">Text</span>
                  <EditableText value={t.text} onSave={(v) => updateTile(i, { text: v })} /></div>
                <div>
                  <span className="text-[11px] uppercase text-neutral-500 font-semibold">Icon</span>
                  <select value={t.icon} onChange={e => updateTile(i, { icon: e.target.value })} className="block w-full mt-1 border border-neutral-300 rounded-sm px-2 py-1 text-sm focus:outline-none focus:border-[#F46A25]">
                    {iconOptions.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white border border-neutral-200 rounded-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-[#12263A]">Footer links</h3>
          <button onClick={addFooterCol} className="text-xs bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold px-3 py-1.5 rounded-sm flex items-center gap-1"><Plus className="w-3 h-3" /> Add column</button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.entries(footerLinks).map(([title, links]) => (
            <div key={title} className="border border-neutral-200 rounded-sm p-3">
              <div className="flex items-start justify-between mb-3 gap-2">
                <div className="flex-1">
                  <span className="text-[11px] uppercase text-neutral-500 font-semibold">Column title</span>
                  <EditableText value={title} onSave={(v) => updateFooterCol(title, v)} className="font-bold" />
                </div>
                <button onClick={() => removeFooterCol(title)} className="text-red-600 hover:bg-red-50 p-1 rounded" aria-label="Remove column"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
              <div className="space-y-1">
                {(links || []).map((l, i) => (
                  <div key={i} className="flex items-center gap-1">
                    <div className="flex-1"><EditableText value={l} onSave={(v) => updateFooterItem(title, i, v)} className="text-sm" /></div>
                    <button onClick={() => removeFooterItem(title, i)} className="text-neutral-400 hover:text-red-600 p-1" aria-label="Remove"><Trash2 className="w-3 h-3" /></button>
                  </div>
                ))}
                <button onClick={() => addFooterItem(title)} className="text-xs text-[#F46A25] font-semibold hover:underline flex items-center gap-1 mt-1"><Plus className="w-3 h-3" /> Add link</button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default SettingsAdmin;
